<script>
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

// Local datetime-local inputs need "YYYY-MM-DDTHH:mm" in local time, not UTC.
const toDateTimeLocalValue = date =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);

export default {
  components: {
    NextInput,
    NextButton,
    ToggleSwitch,
  },
  props: {
    isAWhatsAppChannel: {
      type: Boolean,
      default: false,
    },
    selectedTemplate: {
      type: Object,
      default: null,
    },
    showMessageField: {
      type: Boolean,
      default: true,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    initialScheduledAt: {
      type: Date,
      default: null,
    },
    initialContent: {
      type: String,
      default: '',
    },
    initialCancelOnReply: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'confirm', 'chooseTemplate', 'removeTemplate'],
  data() {
    return {
      scheduledAt: toDateTimeLocalValue(this.initialScheduledAt || new Date()),
      content: this.initialContent,
      cancelOnReply: this.initialCancelOnReply,
    };
  },
  computed: {
    minScheduledAt() {
      return toDateTimeLocalValue(new Date());
    },
    isConfirmDisabled() {
      if (!this.scheduledAt) return true;
      return this.showMessageField && !this.content.trim();
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    onConfirm() {
      this.$emit('confirm', {
        scheduledAt: new Date(this.scheduledAt).toISOString(),
        content: this.content,
        cancelOnReply: this.cancelOnReply,
      });
    },
  },
};
</script>

<template>
  <div class="flex flex-col">
    <woot-modal-header
      :header-title="
        isEditing
          ? $t('CONVERSATION.SCHEDULE_MESSAGE.EDIT_TITLE')
          : $t('CONVERSATION.SCHEDULE_MESSAGE.TITLE')
      "
    />
    <form
      class="modal-content w-full pt-2 px-5 pb-6 flex flex-col gap-4"
      @submit.prevent="onConfirm"
    >
      <NextInput
        v-model="scheduledAt"
        type="datetime-local"
        :min="minScheduledAt"
        :label="$t('CONVERSATION.SCHEDULE_MESSAGE.DATE_PLACEHOLDER')"
      />

      <div v-if="showMessageField">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('CONVERSATION.SCHEDULE_MESSAGE.MESSAGE_LABEL') }}
        </label>
        <textarea
          v-model="content"
          rows="3"
          class="w-full mt-1"
          :placeholder="$t('CONVERSATION.SCHEDULE_MESSAGE.MESSAGE_PLACEHOLDER')"
        />
      </div>

      <div v-if="isAWhatsAppChannel" class="flex flex-col gap-2">
        <p class="text-sm text-n-slate-11 mb-0">
          {{
            showMessageField
              ? $t('CONVERSATION.SCHEDULE_MESSAGE.TEMPLATE_FALLBACK_HINT')
              : $t('CONVERSATION.SCHEDULE_MESSAGE.TEMPLATE_ONLY_HINT')
          }}
        </p>
        <div v-if="selectedTemplate" class="flex items-center gap-2">
          <span class="text-sm text-n-slate-12">{{
            selectedTemplate.name
          }}</span>
          <NextButton
            v-if="showMessageField"
            slate
            faded
            sm
            :label="$t('CONVERSATION.SCHEDULE_MESSAGE.REMOVE_TEMPLATE')"
            @click.prevent="$emit('removeTemplate')"
          />
        </div>
        <NextButton
          v-else-if="showMessageField"
          slate
          faded
          sm
          :label="$t('CONVERSATION.SCHEDULE_MESSAGE.CHOOSE_TEMPLATE')"
          @click.prevent="$emit('chooseTemplate')"
        />
      </div>

      <div class="flex items-center gap-2">
        <ToggleSwitch v-model="cancelOnReply" />
        <span class="text-sm text-n-slate-12">
          {{ $t('CONVERSATION.SCHEDULE_MESSAGE.CANCEL_ON_REPLY') }}
        </span>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('CONVERSATION.SCHEDULE_MESSAGE.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :disabled="isConfirmDisabled"
          :label="
            isEditing
              ? $t('CONVERSATION.SCHEDULE_MESSAGE.SAVE')
              : $t('CONVERSATION.SCHEDULE_MESSAGE.CONFIRM')
          "
        />
      </div>
    </form>
  </div>
</template>
